import React, { FC } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { Icon, Typography, Col, Row } from 'antd'
import { endCustomerService } from '../../services'
import { EndCustomer, EndCustomerFormValues, TabConfig, RequestErrorObject } from '../../types'
import Licenses from './Licenses'
import { Page, AccountActions, RoutedTabs, handleError } from '../../components'
import RequestError from '../../services/RequestError'
import withEntityRequest from '../../components/withEntityRequest'
import Account from './Account'
import { useAuth } from '../../providers/AuthProvider'

const { Text, Title } = Typography

interface RouteParameters {
	id: string
	tab: string
}

interface EndCustomerPageProps {
	entity: EndCustomer
	entityUpdated(entity: EndCustomer): void
}

const EndCustomerPage: FC<EndCustomerPageProps> = ({ entity, entityUpdated }: EndCustomerPageProps) => {
	const history = useHistory()
	const { userHasPermission } = useAuth()

	const tabs: TabConfig[] = [
		{
			id: 'licenses',
			title: (
				<>
					<Icon type='pound' /> Licensing
				</>
			),
			render: (): React.ReactNode => <Licenses isAdmin={userHasPermission(['ServiceProvider.All.ReadWrite'])} endCustomer={entity} />
		},
		{
			id: 'details',
			title: (
				<>
					<Icon type='setting' /> Details
				</>
			),
			render: (): React.ReactNode => <Account endCustomer={entity} submit={editEndCustomer} />
		}
	]

	const deleteEndCustomer = async (): Promise<void> => {
		const createEndCustomerDeleteError = (error: RequestError): RequestErrorObject => {
			const source = 'End Customer Delete'
			const base = { status: error.response.status, source }

			switch (error.response.status) {
				case 500:
					return { ...base, description: `Error deleting ${entity.name}, experienced a server error.` }
				case 409:
					return { ...base, description: `Error deleting ${entity.name}, have you deleted its licenses?` }
				default:
					return { ...base, description: `Error deleting ${entity.name}` }
			}
		}

		try {
			await endCustomerService.delete(entity.id)
			history.replace('/endcustomer')
		} catch (error) {
			handleError(error, createEndCustomerDeleteError)
		}
	}

	const editEndCustomer = async (values: EndCustomerFormValues): Promise<void> => {
		const createEndCustomerUpdateError = (error: RequestError): RequestErrorObject | RequestErrorObject[] => {
			const source = 'End Customer Update'
			const base = { status: error.response.status, source }

			switch (error.response.status) {
				case 422:
					return { ...base, description: `Error updating ${entity.name}. The data sent wasn't correct.` }
				default:
					return { ...base, description: `Error updating ${entity.name}. Please try again or report this.` }
			}
		}

		try {
			const { data } = await endCustomerService.edit(entity.id, values)
			entityUpdated(data)
		} catch (error) {
			handleError(error, createEndCustomerUpdateError)
		}
	}

	return (
		<Page
			title={
				<Title>
					<Row type='flex' justify='space-between'>
						<Col>
							{entity.name} <Text type='secondary'>End Customer</Text>
						</Col>
						<AccountActions deleteAction={deleteEndCustomer} />
					</Row>
				</Title>
			}
		>
			<RoutedTabs tabs={tabs} />
		</Page>
	)
}

const PageWithRequest = withEntityRequest(EndCustomerPage, { getById: id => endCustomerService.getById(id), type: 'End Customer' })

const EndCustomerPageWithRequest = (): JSX.Element => {
	const { id } = useParams<RouteParameters>()
	return <PageWithRequest id={parseInt(id, 10)} />
}

export default EndCustomerPageWithRequest
